import { getVendorDetailByIdAndSite } from '@/api/users/request'
import * as userUtil from '@/api/users/util'
import { PROJECT_TYPES } from '@/modules/projects/consts'
import store from '@/store'
import { EmailTypes, ENGINEER_HOST_NAME } from '@/utils/consts'
import dateUtil from '@/utils/dateUtil'
import i18n from '@/utils/i18n'
import { bidStatusMap, vendorBidStatus } from '@/utils/locale'
import { emailDateFormat } from '@/utils/regex'
import userHelper from '@/utils/userHelper'
import { SEND_EMAIL, SEND_TO_ENGINEER, SEND_TO_VENDOR } from './consts'
import { getEmailDetailByIdAndSite, getVendorDetailAndSubEmails } from './tender.utils'
import { buildEmail, getLink, getSiteInfo, getSubsidiaryTextArray, getVendorHomeLink } from './util'

function getTenderInfo (tender) {
  const tenderInfo = { pic: {}, approver: {} }
  tenderInfo.tenderType = tender.general.tenderType
  tenderInfo.prNumber = tender.general.prNumber
  tenderInfo.numbers = tender.general.numbers
  tenderInfo.isPurchaseOrder = tender.general.numberType === PROJECT_TYPES.PO
  tenderInfo.prDescription = tender.general.prDescription
  const closingDateTime = tender.general.closingDate ? dateUtil.convertDateToArray(
    tender.general.closingDate, emailDateFormat, ' ') : null
  tenderInfo.closingDate = closingDateTime ? `${closingDateTime[1]} ${closingDateTime[0]}` : ''
  tenderInfo.onsiteVisitRequired = tender.general.onsiteVisitRequired
  tenderInfo.onsiteContact = tender.general.onsiteContact
  tenderInfo.onsiteLocation = tender.general.onsiteLocation
  tenderInfo.version = tender.general.version
  tenderInfo.id = tender.general.id
  tenderInfo.extendClosingDateReason = tender.general.extendClosingDateReason

  if (tender.general.manager) {
    const picEmail = userUtil.getPrimaryEmail(tender.general.manager)
    const picName = userHelper.getUserName(tender.general.manager.username.split('@')[0], deep(tender, 'general.manager.displayName'))
    const picTitle = deep(tender, 'general.manager.title')

    if (picEmail && picEmail.indexOf('@') > -1) {
      tenderInfo.pic.email = picEmail
      tenderInfo.pic.name = picName
      tenderInfo.pic.title = picTitle
    }
  }

  if (tender.general.approver) {
    const approverEmail = userUtil.getPrimaryEmail(tender.general.approver)
    const approverName = userHelper.getUserName(tender.general.approver.username.split('@')[0], deep(tender, 'general.approver.displayName'))
    const approverTitle = deep(tender, 'general.approver.title')
    if (approverEmail && approverEmail.indexOf('@') > -1) {
      tenderInfo.approver.email = approverEmail
      tenderInfo.approver.name = approverName
      tenderInfo.approver.title = approverTitle
    }
  }

  return tenderInfo
}

function isValidVendor (vendorDetail) {
  return vendorDetail && vendorDetail.email && vendorDetail.enabled
}

const tenderNotification = {
  writeExtendNotifyVendorEmail: async (tender) => {
    const emails = []
    const invalidVendors = []
    const tenderInfo = getTenderInfo(tender)
    const siteCode = tender.general.site
    const siteInfo = await getSiteInfo(siteCode)

    const emailDetails = await Promise.all(
      tender.bids.map(async (bid) => {
        if (bidStatusMap[bid.status] !== bidStatusMap.NO_SHOW) {
          return []
        }
        const [vendorDetail, subAccountEmails] = await getEmailDetailByIdAndSite(bid.vendorId, siteCode)
        if (!vendorDetail || !vendorDetail.email || !vendorDetail.enabled) {
          invalidVendors.push(deep(bid, 'company.name') || '')
        }
        if (bid.visitDate) {
          const [visitDay, visitTime] = dateUtil.convertDateToArray(bid.visitDate, emailDateFormat, ' ')
          vendorDetail.visitDate = visitDay.trim()
          vendorDetail.visitTime = visitTime.trim()
        }
        return [vendorDetail, subAccountEmails]
      }),
    )

    if (invalidVendors.length > 0) {
      const message = i18n.t('error.sendmail.contactInfoMissing', [ invalidVendors.join('\n\t') ])
      throw new Error(message)
    }

    emailDetails.forEach(item => {
      const template = EmailTypes.tenders.extendNotifyVendor
      const [vendorDetail, vendorSubEmails] = item
      const metadata = { tenderLink: getVendorHomeLink(), vendorDetail, tenderInfo, siteInfo }
      metadata.cc = vendorSubEmails
      if (vendorDetail) {
        buildEmail(emails, vendorDetail.email, template, metadata, SEND_TO_VENDOR)
      }
    })
    return emails
  },

  writeExtendNotifyEngineerEmail: async (tender) => {
    const emails = []
    const tenderInfo = getTenderInfo(tender)
    const toEmail = tenderInfo.pic.email
    const vendorList = []

    await Promise.all(
      tender.bids.map(async (bid) => {
        if (bidStatusMap[bid.status] === bidStatusMap.NO_SHOW) {
          const [vendorDetail] = await getVendorDetailByIdAndSite(bid.vendorId, tender.general.site)
          if (vendorDetail && vendorDetail.email && vendorDetail.enabled) {
            vendorList.push(deep(bid, 'company.name'))
          }
        }
      }),
    )
    const metadata = {
      toEmail,
      personName: tenderInfo.pic.name,
      tenderInfo,
      vendorList,
    }
    if (toEmail) {
      const template = EmailTypes.tenders.extendNotifyEngineer
      buildEmail(emails, toEmail, template, metadata, SEND_TO_ENGINEER)
    }
    return emails
  },

  writeIssueNoticeEmail: async (isAddendum = false) => {
    const emails = []
    const invalidVendors = []
    const currentTender = store.getters['tenders/currentTender']
    const tenderInfo = getTenderInfo(currentTender)
    const siteCode = currentTender.general.site
    const siteInfo = await getSiteInfo(siteCode)

    const emailDetails = await Promise.all(
      currentTender.bids.map(async (bid) => {
        if (isAddendum && bid.status === vendorBidStatus.DECLINED) {
          return []
        }
        const [vendorDetail, subAccountEmails] = await getEmailDetailByIdAndSite(bid.vendorId, siteCode)
        if (!isValidVendor(vendorDetail)) {
          invalidVendors.push(deep(bid, 'company.name') || '')
        }
        if (bid.visitDate) {
          const [visitDay, visitTime] = dateUtil.convertDateToArray(bid.visitDate, emailDateFormat, ' ')
          vendorDetail.visitDate = visitDay.trim()
          vendorDetail.visitTime = visitTime.trim()
        }
        return [vendorDetail, subAccountEmails]
      }),
    )

    if (!isAddendum && invalidVendors.length > 0) {
      const message = i18n.t('error.sendmail.contactInfoMissing', [ invalidVendors.join('\n\t') ])
      throw new Error(message)
    }

    const template = isAddendum ? EmailTypes.tenders.addendum : EmailTypes.tenders.invitation
    const metadata = { tenderLink: getVendorHomeLink(), tenderInfo, siteInfo }
    emailDetails.forEach(item => {
      const [vendorDetail, vendorSubEmails] = item
      metadata.cc = vendorSubEmails
      if (isValidVendor(vendorDetail)) {
        buildEmail(emails, vendorDetail.email, template, { ...metadata, vendorDetail }, SEND_TO_VENDOR)
      }
    })
    return emails
  },

  writeApprovalResultEmail: async (emailType, rejectInfo) => {
    const emails = []
    const action = rejectInfo ? 'reject' : 'approve'
    const rejectReason = rejectInfo ? rejectInfo.rejectReason : null
    const isAddendum = rejectInfo && rejectInfo.isAddendum
    const currentTender = store.getters['tenders/currentTender']
    const viewRoute = rejectReason && isAddendum ? 'tenderAddendumDetailsView' : 'tenderDetailsView'
    const tenderLink = getLink(viewRoute, currentTender.general.id)
    const tenderInfo = {}
    tenderInfo.action = action
    tenderInfo.prNumber = currentTender.general.prNumber
    tenderInfo.prDescription = currentTender.general.prDescription
    const toEmail = userUtil.getPrimaryEmail(currentTender.general.manager)
    const picName = userHelper.getUserName(currentTender.general.manager.username.split('@')[0], deep(currentTender, 'general.manager.displayName'))
    const approverName = userHelper.getUserName(currentTender.general.approver.username.split('@')[0], deep(currentTender, 'general.approver.displayName'))
    const metadata = {
      toEmail,
      personName: picName,
      tenderInfo,
      approver: `${approverName} <${userUtil.getPrimaryEmail(currentTender.general.approver)}>`,
      rejectReason,
      tenderLink,
    }

    if (toEmail) {
      buildEmail(emails, toEmail, emailType, metadata, SEND_TO_ENGINEER)
    }
    return emails
  },

  writeAwardResultEmail: async (extMetaData = {}) => {
    const emails = []
    const currentTender = store.getters['tenders/currentTender']
    const tenderInfo = getTenderInfo(currentTender)
    const cc = tenderInfo.pic.email
    const siteCode = currentTender.general.site
    const siteInfo = await getSiteInfo(siteCode)
    const awardedId = extMetaData.awardedId
    await Promise.all(
      currentTender.bids.map(async (bid) => {
        if (vendorBidStatus.DECLINED !== bid.status) {
          const [vendorDetail, subAccountEmails] = await getEmailDetailByIdAndSite(bid.vendorId, siteCode)
          const template = awardedId === bid.id ? EmailTypes.tenders.awardVendor : EmailTypes.tenders.regretVendor
          const metadata = {
            vendorDetail,
            tenderInfo,
            siteInfo,
          }
          metadata.cc = subAccountEmails
          if (awardedId === bid.id) {
            metadata.cc.push(cc)
            metadata.docIds = extMetaData.docIds
          }
          if (vendorDetail) {
            buildEmail(emails, vendorDetail.email, template, metadata, SEND_TO_VENDOR)
          }
          return null
        }
      }),
    )
    return emails
  },

  writeSubmitBidEmail: async (emailType, extMetaData) => {
    const emails = []
    const currentTender = store.getters['tenders/currentTender']
    const siteCode = currentTender.general.site
    const [vendorDetail, subAccountEmails] = await getVendorDetailAndSubEmails(siteCode)
    const tenderInfo = getTenderInfo(currentTender)
    const [respondedDate, respondedTime] = dateUtil.convertDateToArray(extMetaData.respondedOn, emailDateFormat, ' ')
    tenderInfo.respondedDate = respondedDate
    tenderInfo.respondedTime = respondedTime
    if (extMetaData.version) {
      tenderInfo.version = extMetaData.version
    }
    const metadata = {
      cc: subAccountEmails,
      tenderInfo,
      vendorDetail,
      isSubmission: extMetaData.isSubmission,
    }
    if (vendorDetail) {
      buildEmail(emails, vendorDetail.email, emailType, metadata, SEND_EMAIL)
    }
    return emails
  },

  writeWithdrawBidEmail: async () => {
    const emails = []
    const currentTender = store.getters['tenders/currentTender']
    const siteCode = currentTender.general.site
    const [vendorDetail, subAccountEmails] = await getVendorDetailAndSubEmails(siteCode)
    const tenderInfo = getTenderInfo(currentTender)
    const respondedOn = new Date(Date.now())
    const [respondedDate, respondedTime] = dateUtil.convertDateToArray(respondedOn, emailDateFormat, ' ')
    tenderInfo.respondedDate = respondedDate
    tenderInfo.respondedTime = respondedTime
    const metadata = {
      cc: subAccountEmails,
      tenderInfo,
      vendorDetail,
    }
    metadata.bcc = [tenderInfo.pic.email]
    if (vendorDetail) {
      buildEmail(emails, vendorDetail.email, EmailTypes.tenders.withdrawBid, metadata, SEND_EMAIL)
    }
    return emails
  },

  writeSubmitForApprovalEmail: () => {
    const emails = []
    const currentTender = store.getters['tenders/currentTender']
    const tenderInfo = getTenderInfo(currentTender)
    const tenderLink = getLink('tenderDetailsView', currentTender.general.id)
    const metadata = {
      tenderInfo,
      tenderLink,
    }

    buildEmail(emails, tenderInfo.approver.email, EmailTypes.tenders.submitForApproval, metadata, SEND_EMAIL)
    return emails
  },

  writeQueryEmail: async (extMetaData) => {
    const emails = []
    const currentTender = store.getters['tenders/currentTender']
    const tenderInfo = getTenderInfo(currentTender)
    const siteCode = currentTender.general.site
    const siteInfo = await getSiteInfo(siteCode)
    const subsidiaryArray = getSubsidiaryTextArray(siteInfo.subsidiary)
    const subsidiary = subsidiaryArray.join(', ')
    const tqClosingDate = extMetaData.closingDate ? dateUtil.convertDateToArray(extMetaData.closingDate, emailDateFormat, ' ') : null

    const bid = currentTender.bids.filter(bid => bid.id === extMetaData.bidId)[0]
    const [vendorDetail, subAccountEmails] = await getEmailDetailByIdAndSite(bid.vendorId, siteCode)
    const metadata = {
      cc: subAccountEmails,
      vendorDetail,
      tenderInfo: { tenderQueryClosingDate: tqClosingDate ? `${tqClosingDate[1]} ${tqClosingDate[0]}` : '', queryNumber: extMetaData.order, ...tenderInfo },
      tenderLink: getVendorHomeLink(),
      siteInfo,
      subsidiary,
      subsidiaryArray,
    }
    buildEmail(emails, vendorDetail.email, EmailTypes.tenders.query, metadata, SEND_EMAIL)

    return emails
  },

  writeQueryResponseEmail: async (extMetaData) => {
    const emails = []
    const currentTender = store.getters['tenders/currentTender']
    const siteCode = currentTender.general.site
    const vendorDetail = await userUtil.getCurrentVendorAdmin(siteCode)
    const tenderInfo = getTenderInfo(currentTender)
    const tenderLink = getLink('queryStatus', currentTender.general.id, ENGINEER_HOST_NAME)

    const picName = userHelper.getUserName(currentTender.general.manager.username.split('@')[0], deep(currentTender, 'general.manager.displayName'))
    const metadata = {
      personName: picName,
      vendorDetail,
      tenderInfo: { queryNumber: extMetaData.order, ...tenderInfo },
      tenderLink,
    }

    buildEmail(emails, tenderInfo.pic.email, EmailTypes.tenders.queryResponse, metadata, SEND_EMAIL)
    return emails
  },

  writeSubmitQueryEmail: async (extMetaData) => {
    const emails = []
    const currentTender = store.getters['tenders/currentTender']
    const siteCode = currentTender.general.site
    const [vendorDetail, subAccountEmails] = await getVendorDetailAndSubEmails(siteCode)
    const tenderInfo = getTenderInfo(currentTender)
    const [respondedDate, respondedTime] = dateUtil.convertDateToArray(extMetaData.submittedDate || new Date(), emailDateFormat, ' ')
    tenderInfo.respondedDate = respondedDate
    tenderInfo.respondedTime = respondedTime
    tenderInfo.queryNumber = extMetaData.order

    const metadata = {
      cc: subAccountEmails,
      tenderInfo,
      vendorDetail,
    }
    buildEmail(emails, vendorDetail.email, EmailTypes.tenders.submitQuery, metadata, SEND_EMAIL)
    return emails
  },
}

export default tenderNotification
